import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import { Badge, Collapse, List, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import CodeIcon from "@material-ui/icons/Code";
import LabelIcon from "@material-ui/icons/Label";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import BlurLinearIcon from "@material-ui/icons/BlurLinear";
import AspectRatioIcon from "@material-ui/icons/AspectRatio";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import SendIcon from "@material-ui/icons/Send";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import BugReportIcon from "@material-ui/icons/BugReport";
import HistoryIcon from "@material-ui/icons/History";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";

/* ==========================================================================
   CRM LASIT — MENÚ LATERAL PREMIUM v2.0
   PROHIBIDO MODIFICAR SIN AUTORIZACIÓN  |  Trae AI  |  2026-05-13
   ========================================================================== */

// Colores del sistema
const C = {
  accent:      "#00D4AA",
  accentDim:   "rgba(0,212,170,0.12)",
  accentBord:  "rgba(0,212,170,0.25)",
  text:        "#FFFFFF",
  textMuted:   "rgba(255,255,255,0.5)",
  textDim:     "rgba(255,255,255,0.28)",
  activeBg:    "linear-gradient(90deg, rgba(0,212,170,0.18) 0%, rgba(0,212,170,0.04) 100%)",
  hoverBg:     "rgba(0,212,170,0.07)",
  subheader:   "rgba(255,255,255,0.22)",
};

// Colores de íconos por sección
const ICON_COLORS = {
  dashboard:   "#00D4AA",
  agente:      "#FF9800",
  conexiones:  "#4FC3F7",
  masivo:      "#BA68C8",
  chat:        "#25D366",
  contactos:   "#64B5F6",
  settings:    "#90A4AE",
  users:       "#FFB74D",
  lab:         "#EF9A9A",
  gemini:      "#7986CB",
  etiquetas:   "#4DB6AC",
  campos:      "#AED581",
  logs:        "#BCAAA4",
  campaigns:   "#F06292",
  bulkMsg:     "#80CBC4",
  transmision: "#FFD54F",
};

const useStyles = makeStyles(() => ({
  listItem: {
    borderRadius: 10,
    margin: "2px 8px",
    width: "calc(100% - 16px)",
    padding: "8px 12px",
    transition: "all 0.18s ease",
    color: C.textMuted,
    "&:hover": {
      background: C.hoverBg,
      color: C.text,
      "& .MuiListItemIcon-root": { color: "inherit" },
    },
  },
  listItemActive: {
    background: C.activeBg,
    color: C.text,
    borderLeft: `3px solid ${C.accent}`,
    paddingLeft: 9, // compensar el border
    "& .MuiListItemIcon-root": { color: `${C.accent} !important` },
    "&:hover": { background: C.activeBg },
  },
  listItemIcon: {
    minWidth: 38,
    transition: "color 0.18s",
  },
  listItemText: {
    "& .MuiTypography-body1": {
      fontSize: "0.87rem",
      fontWeight: 500,
      fontFamily: "system-ui, -apple-system, sans-serif",
    },
  },
  listItemTextBold: {
    "& .MuiTypography-body1": {
      fontWeight: 700,
      color: C.text,
    },
  },

  // Sub-ítems (dentro de collapse)
  subItem: {
    borderRadius: 8,
    margin: "1px 8px 1px 20px",
    width: "calc(100% - 28px)",
    padding: "7px 12px",
    color: C.textDim,
    transition: "all 0.15s ease",
    "&:hover": {
      background: C.hoverBg,
      color: C.text,
    },
  },
  subItemActive: {
    background: "rgba(0,212,170,0.08)",
    color: C.accent,
    "& .MuiListItemIcon-root": { color: `${C.accent} !important` },
  },
  subItemIcon: {
    minWidth: 32,
    "& svg": { fontSize: "1.1rem" },
  },
  subItemText: {
    "& .MuiTypography-body1": {
      fontSize: "0.82rem",
      fontWeight: 500,
      fontFamily: "system-ui, -apple-system, sans-serif",
    },
  },

  // Subheader
  subheader: {
    color: C.subheader,
    fontSize: "0.67rem",
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    lineHeight: 1,
    padding: "20px 20px 8px",
    fontFamily: "system-ui, -apple-system, sans-serif",
    background: "transparent",
  },

  // Divisor sutil
  divider: {
    height: 1,
    background: "rgba(0,212,170,0.08)",
    margin: "8px 16px",
    border: "none",
  },

  // Badge de advertencia
  warnBadge: {
    "& .MuiBadge-badge": {
      background: "#FF6B6B",
      color: "#fff",
      fontSize: "0.6rem",
      fontWeight: 700,
      minWidth: 16,
      height: 16,
      borderRadius: 8,
    },
  },

  // Expand arrows
  expandIcon: {
    color: C.textDim,
    fontSize: "1.1rem",
    transition: "transform 0.2s",
  },
}));

// ─── Componente de ítem de lista ────────────────────────────────────────────
function NavItem({ icon, primary, to, className, subItem, onClick, extraIcon, drawerOpen }) {
  const classes = useStyles();
  const location = useLocation();
  const isActive = to && (location.pathname === to || location.pathname.startsWith(to + "/"));

  const cls = subItem
    ? `${classes.subItem} ${isActive ? classes.subItemActive : ""} ${className || ""}`
    : `${classes.listItem} ${isActive ? classes.listItemActive : ""} ${className || ""}`;

  const iconCls = subItem ? classes.subItemIcon : classes.listItemIcon;
  const textCls = subItem ? classes.subItemText : `${classes.listItemText} ${isActive ? classes.listItemTextBold : ""}`;

  const renderLink = React.useMemo(
    () =>
      to
        ? React.forwardRef((itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />)
        : undefined,
    [to]
  );

  // Cuando está colapsado: centrar íconos, ocultar texto
  const collapsedStyle = !drawerOpen ? {
    justifyContent: "center",
    padding: "10px 0",
    margin: "2px 4px",
    width: "calc(100% - 8px)",
  } : {};

  return (
    <ListItem
      button
      component={renderLink || "div"}
      className={cls}
      onClick={onClick}
      disableGutters
      style={collapsedStyle}
    >
      {icon && (
        <ListItemIcon
          className={iconCls}
          style={!drawerOpen ? { minWidth: 0, justifyContent: "center" } : {}}
        >
          {icon}
        </ListItemIcon>
      )}
      {/* Ocultar texto cuando drawer está colapsado */}
      {drawerOpen && (
        <ListItemText primary={primary} className={textCls} />
      )}
      {drawerOpen && extraIcon}
    </ListItem>
  );
}


// ─── Componente Principal ────────────────────────────────────────────────────
const MainListItems = ({ drawerClose, drawerOpen }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [openMasivo, setOpenMasivo] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (whatsApps?.length > 0) {
        const offline = whatsApps.filter((w) =>
          ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
        );
        setConnectionWarning(offline.length > 0);
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [whatsApps]);

  const hasPerm = (mod) => {
    if (user.profile === "admin") return true;
    if (!user.permissions) return false;
    let p = user.permissions;
    if (typeof p === "string") { try { p = JSON.parse(p); } catch { return false; } }
    return p[mod] === true;
  };

  // Wrapper para cerrar drawer en móvil
  const onNav = () => drawerClose && drawerClose();

  // Si el drawer está cerrado, envolver en tooltip
  const maybeTooltip = (label, child) =>
    drawerOpen ? child : <Tooltip title={label} placement="right" key={label}>{child}</Tooltip>;

  return (
    <div>
      {/* DASHBOARD */}
      {hasPerm("dashboard") && maybeTooltip("Dashboard",
        <NavItem
          to="/"
          primary="Dashboard"
          drawerOpen={drawerOpen}
          icon={<DashboardOutlinedIcon style={{ color: ICON_COLORS.dashboard, fontSize: "1.25rem" }} />}
          onClick={onNav}
        />
      )}

      {/* AGENTE */}
      {hasPerm("agente") && maybeTooltip("Agente",
        <NavItem
          to="/agente"
          primary="Agente"
          drawerOpen={drawerOpen}
          icon={<HeadsetMicIcon style={{ color: ICON_COLORS.agente, fontSize: "1.25rem" }} />}
          onClick={onNav}
        />
      )}

      {/* CONEXIONES */}
      {hasPerm("connections") && maybeTooltip("Conexiones",
        <NavItem
          to="/connections"
          primary={i18n.t("mainDrawer.listItems.connections")}
          drawerOpen={drawerOpen}
          icon={
            <Badge
              badgeContent={connectionWarning ? "!" : 0}
              color="error"
              overlap="rectangular"
              className={classes.warnBadge}
            >
              <SyncAltIcon style={{ color: ICON_COLORS.conexiones, fontSize: "1.25rem" }} />
            </Badge>
          }
          onClick={onNav}
        />
      )}

      {/* MASIVO */}
      {hasPerm("masivo") && (
        <>
          {maybeTooltip("Masivo",
            <NavItem
              primary="Masivo"
              drawerOpen={drawerOpen}
              icon={<BlurLinearIcon style={{ color: ICON_COLORS.masivo, fontSize: "1.25rem" }} />}
              onClick={() => setOpenMasivo(!openMasivo)}
              extraIcon={
                openMasivo
                  ? <ExpandLess className={classes.expandIcon} />
                  : <ExpandMore className={classes.expandIcon} />
              }
            />
          )}
          {/* Sub-ítems solo visibles cuando el drawer está abierto */}
          {drawerOpen && (
            <Collapse in={openMasivo} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <NavItem drawerOpen subItem to="/campaigns-ads"  primary="Campañas Publicitarias" icon={<AspectRatioIcon style={{ color: ICON_COLORS.campaigns,  fontSize: "1.1rem" }} />} onClick={onNav} />
                <NavItem drawerOpen subItem to="/bulk-messages"  primary="Mensajes Masivos"       icon={<ViewModuleIcon  style={{ color: ICON_COLORS.bulkMsg,     fontSize: "1.1rem" }} />} onClick={onNav} />
                <NavItem drawerOpen subItem to="/transmission"   primary="Transmisión"            icon={<SendIcon        style={{ color: ICON_COLORS.transmision, fontSize: "1.1rem" }} />} onClick={onNav} />
              </List>
            </Collapse>
          )}
        </>
      )}

      {/* CHAT */}
      {hasPerm("tickets") && maybeTooltip("Chat",
        <NavItem
          to="/tickets"
          primary="Chat"
          drawerOpen={drawerOpen}
          icon={<WhatsAppIcon style={{ color: ICON_COLORS.chat, fontSize: "1.25rem" }} />}
          onClick={onNav}
        />
      )}

      {/* CONTACTOS */}
      {hasPerm("contacts") && maybeTooltip("Contactos",
        <NavItem
          to="/contacts"
          primary={i18n.t("mainDrawer.listItems.contacts")}
          drawerOpen={drawerOpen}
          icon={<ContactPhoneOutlinedIcon style={{ color: ICON_COLORS.contactos, fontSize: "1.25rem" }} />}
          onClick={onNav}
        />
      )}

      {/* ── SECCIÓN ADMINISTRACIÓN ── */}
      {hasPerm("settings") && (
        <Can
          role={user.profile}
          perform="drawer-admin-items:view"
          yes={() => (
            <>
              <hr className={classes.divider} />
              <ListSubheader className={classes.subheader} disableSticky>
                {drawerOpen ? i18n.t("mainDrawer.listItems.administration") : "●●●"}
              </ListSubheader>

              {maybeTooltip("Configuración",
                <NavItem
                  primary={i18n.t("mainDrawer.listItems.settings")}
                  drawerOpen={drawerOpen}
                  icon={<SettingsOutlinedIcon style={{ color: ICON_COLORS.settings, fontSize: "1.25rem" }} />}
                  onClick={() => setOpenSettings(!openSettings)}
                  extraIcon={
                    openSettings
                      ? <ExpandLess className={classes.expandIcon} />
                      : <ExpandMore className={classes.expandIcon} />
                  }
                />
              )}

              {drawerOpen && (
                <Collapse in={openSettings} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <NavItem drawerOpen subItem to="/users"           primary={i18n.t("mainDrawer.listItems.users")} icon={<PeopleAltOutlinedIcon style={{ color: ICON_COLORS.users,     fontSize: "1.1rem" }} />} onClick={onNav} />
                    <NavItem drawerOpen subItem to="/settings/lab"    primary="Laboratorio"  icon={<BugReportIcon  style={{ color: ICON_COLORS.lab,       fontSize: "1.1rem" }} />} onClick={onNav} />
                    <NavItem drawerOpen subItem to="/settings/gemini" primary="Gemini"       icon={<CodeIcon       style={{ color: ICON_COLORS.gemini,    fontSize: "1.1rem" }} />} onClick={onNav} />
                    <NavItem drawerOpen subItem to="/settings/tags"   primary="Etiquetas"    icon={<LabelIcon      style={{ color: ICON_COLORS.etiquetas, fontSize: "1.1rem" }} />} onClick={onNav} />
                    <NavItem drawerOpen subItem to="/settings/fields" primary="Campos"       icon={<TextFieldsIcon style={{ color: ICON_COLORS.campos,    fontSize: "1.1rem" }} />} onClick={onNav} />
                    <NavItem drawerOpen subItem to="/settings/system-logs" primary="Logs del sistema" icon={<HistoryIcon style={{ color: ICON_COLORS.logs, fontSize: "1.1rem" }} />} onClick={onNav} />
                  </List>
                </Collapse>
              )}
            </>
          )}
        />
      )}
    </div>
  );
};

export default MainListItems;
